import { useState, useEffect, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Calculator, Clock, TrendingUp, Users } from "lucide-react";

export function ROICalculator() {
  const [teamSize, setTeamSize] = useState(5);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [sprintLengthWeeks, setSprintLengthWeeks] = useState(2);
  const [reqsPerSprintOverride, setReqsPerSprintOverride] = useState<number | null>(null);

  const [engineersPerPM, setEngineersPerPM] = useState(10);
  const [engineersInvolvedPerClarification, setEngineersInvolvedPerClarification] = useState(
    Math.max(1, Math.round(10 * 0.5))
  );
  const [meetingMinsPerClarification, setMeetingMinsPerClarification] = useState(15);
  const [reworkHrsPerClarification, setReworkHrsPerClarification] = useState(2);
  const [qaMultiplier, setQaMultiplier] = useState(1.2);
  const [engInvolvedTouched, setEngInvolvedTouched] = useState(false);

  useEffect(() => {
    if (engInvolvedTouched) {
      setEngineersInvolvedPerClarification((prev) => Math.min(prev, engineersPerPM));
      return;
    }
    setEngineersInvolvedPerClarification(Math.max(1, Math.round(engineersPerPM * 0.5)));
  }, [engineersPerPM, engInvolvedTouched]);

  const PM_HOURLY = 75;
  const ENG_HOURLY = 90;
  const SPRINTS_PER_YEAR = Math.floor(52 / sprintLengthWeeks);
  const hoursPerSprintSaved = Math.round(teamSize * 1.5);
  const reqsPerPMDefault = 3;
  const reqsPerPMPerSprintFinal = reqsPerSprintOverride ?? reqsPerPMDefault;
  const reqsPerPMPerSprint = reqsPerSprintOverride ?? reqsPerPMDefault;
  const reqsPerSprint = reqsPerPMPerSprintFinal * teamSize;
  const requirementsPerMonth = Math.round((reqsPerSprint * SPRINTS_PER_YEAR) / 12);

  const clarificationsAvoidedPerPMSprint =
    reqsPerSprintOverride == null ? 3.2 : reqsPerPMPerSprintFinal * 1.2;
  const clarifyingQuestionsAvoided = Math.round(clarificationsAvoidedPerPMSprint * teamSize);
  const annualHoursSaved = hoursPerSprintSaved * SPRINTS_PER_YEAR;
  const clarificationsAvoidedPerYear = clarifyingQuestionsAvoided * SPRINTS_PER_YEAR;
  const engInvolved = Math.min(engineersPerPM, engineersInvolvedPerClarification);
  const effectiveEngInvolved = 1 + (engInvolved - 1) * 0.35;

  const pmMeetingHrsSavedRaw = (clarificationsAvoidedPerYear * meetingMinsPerClarification) / 60;
  const pmMeetingHrsSaved = Math.min(pmMeetingHrsSavedRaw, annualHoursSaved);
  const pmTotalHrsSaved = annualHoursSaved + pmMeetingHrsSaved;
  const engMeetingHrsSaved = (clarificationsAvoidedPerYear * meetingMinsPerClarification * effectiveEngInvolved) / 60;
  const engReworkHrsSaved = clarificationsAvoidedPerYear * reworkHrsPerClarification * effectiveEngInvolved;
  const engTotalHrsSaved = engMeetingHrsSaved + engReworkHrsSaved;
  const qaHrsSaved = (pmTotalHrsSaved + engTotalHrsSaved) * (qaMultiplier - 1);
  const totalAnnualHrsSaved = pmTotalHrsSaved + engTotalHrsSaved + qaHrsSaved;
  const totalHrsPerSprintSaved = totalAnnualHrsSaved / SPRINTS_PER_YEAR;

  const pmValueSaved = pmTotalHrsSaved * PM_HOURLY;
  const engValueSaved = engTotalHrsSaved * ENG_HOURLY;
  const qaValueSaved = (pmValueSaved + engValueSaved) * (qaMultiplier - 1);
  const totalValueSaved = Math.round(pmValueSaved + engValueSaved + qaValueSaved);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-14 md:py-20 bg-card/50">
      <div className="container px-4 md:px-6">
        <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
              <Calculator className="w-3.5 h-3.5" />
              ROI Calculator
            </div>
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">See your potential savings</h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Adjust your team size to see how much time Reqqy can save your product team.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Team Size Slider */}
            <div className="glass rounded-xl p-6 mb-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Product Team Size</span>
                </div>
                <div className="text-2xl font-display font-bold text-primary">
                  {teamSize} <span className="text-sm text-muted-foreground">people</span>
                </div>
              </div>
              <Slider
                value={[teamSize]}
                onValueChange={(value) => setTeamSize(value[0])}
                min={1}
                max={50}
                step={1}
                className="w-full"
                aria-label="Product team size"
              />
              <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                <span>1 person</span>
                <span>50 people</span>
              </div>
            </div>

            {/* Advanced assumptions */}
            <Accordion
              type="single"
              collapsible
              value={advancedOpen ? "advanced-assumptions" : undefined}
              onValueChange={(value) => setAdvancedOpen(value === "advanced-assumptions")}
              className="mb-5"
            >
              <AccordionItem value="advanced-assumptions" className="glass rounded-xl overflow-hidden border-0 px-0">
                <AccordionTrigger className="px-6 py-4 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline data-[state=open]:text-foreground [&>svg]:h-3.5 [&>svg]:w-3.5">
                  <span>Additional assumptions (optional)</span>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6 border-t border-border/50 space-y-6">
                  <p className="text-[11px] text-muted-foreground pt-3">
                    Defaults are conservative. These assumptions are <span className="text-foreground font-medium">per PM</span>. Totals are calculated across {teamSize} PMs.
                  </p>

                  <AssumptionSlider label="Engineers per PM" value={engineersPerPM} onChange={setEngineersPerPM} min={3} max={20} step={1} unit="" />
                  <AssumptionSlider
                    label="Engineers involved per clarification loop"
                    value={engineersInvolvedPerClarification}
                    onChange={(v) => { setEngInvolvedTouched(true); setEngineersInvolvedPerClarification(v); }}
                    min={1}
                    max={engineersPerPM}
                    step={1}
                    unit=""
                  />
                  <AssumptionSlider label="Meeting time per clarification" value={meetingMinsPerClarification} onChange={setMeetingMinsPerClarification} min={5} max={60} step={5} unit="mins" />
                  <AssumptionSlider label="Engineering rework per clarification" value={reworkHrsPerClarification} onChange={setReworkHrsPerClarification} min={0} max={8} step={1} unit="hrs" />
                  <AssumptionSlider label="Reqs reviewed per PM per sprint" value={reqsPerSprintOverride ?? reqsPerSprint / teamSize} onChange={(v) => setReqsPerSprintOverride(v)} min={3} max={20} step={1} unit="reqs" />
                  <p className="text-[11px] text-muted-foreground">
                    Total per sprint = {reqsPerPMPerSprint} × {teamSize} PMs
                  </p>
                  <button type="button" onClick={() => setReqsPerSprintOverride(null)} className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-4">
                    Reset to auto-estimate
                  </button>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Sprint length</span>
                      <span className="text-xl font-display font-bold text-primary">
                        {sprintLengthWeeks} <span className="text-xs text-muted-foreground">weeks</span>
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map((w) => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => setSprintLengthWeeks(w)}
                          className={`rounded-lg py-2.5 text-xs font-medium transition-colors ${
                            sprintLengthWeeks === w
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted/60 text-muted-foreground hover:text-foreground"
                          }`}
                          aria-pressed={sprintLengthWeeks === w}
                        >
                          {w}w
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">QA & downstream multiplier</span>
                      <span className="text-xl font-display font-bold text-primary">×{qaMultiplier.toFixed(1)}</span>
                    </div>
                    <Slider
                      value={[Math.round(qaMultiplier * 10)]}
                      onValueChange={(v) => setQaMultiplier(v[0] / 10)}
                      min={10}
                      max={20}
                      step={1}
                      className="w-full"
                      aria-label="QA multiplier"
                    />
                    <div className="flex justify-between mt-1.5 text-[11px] text-muted-foreground">
                      <span>×1.0 (none)</span>
                      <span>×2.0 (high churn)</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Results Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ResultCard icon={Clock} value={Math.round(totalHrsPerSprintSaved)} unit="hrs" label="Saved per sprint" isVisible={isVisible} delay={100} />
              <ResultCard icon={TrendingUp} value={Math.round(totalAnnualHrsSaved)} unit="hrs" label="Saved annually" isVisible={isVisible} delay={200} highlight />
              <ResultCard icon={Calculator} value={requirementsPerMonth} unit="" label="Requirements reviewed/mo" isVisible={isVisible} delay={300} />
              <ResultCard icon={Users} value={clarifyingQuestionsAvoided} unit="" label="Questions avoided/sprint" isVisible={isVisible} delay={400} />
            </div>

            {/* ROI Summary */}
            <div className={`mt-5 p-5 rounded-xl gradient-primary text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="text-primary-foreground/80 text-sm mb-1.5">
                Based on conservative rates across PM, Engineering, and QA, your team could save
              </p>
              <p className="text-3xl font-display font-bold text-primary-foreground">
                ${totalValueSaved.toLocaleString()}
                <span className="text-base font-normal text-primary-foreground/80"> per year</span>
              </p>
              <p className="text-primary-foreground/70 text-xs mt-1.5">
                Includes downstream Engineering rework + QA churn avoided.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ResultCardProps {
  icon: React.ElementType;
  value: number;
  unit: string;
  label: string;
  isVisible: boolean;
  delay: number;
  highlight?: boolean;
}

function ResultCard({ icon: Icon, value, unit, label, isVisible, delay, highlight }: ResultCardProps) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 1000;
    const steps = 30;
    const stepValue = value / steps;
    let current = 0;
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        current += stepValue;
        if (current >= value) {
          setDisplayValue(value);
          clearInterval(interval);
        } else {
          setDisplayValue(Math.round(current));
        }
      }, duration / steps);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [isVisible, value, delay]);

  return (
    <div className={`p-4 rounded-xl transition-all duration-300 ${highlight ? "glass border border-primary/20" : "glass border border-border/30"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-primary" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className={`text-2xl font-display font-bold ${highlight ? "text-primary" : ""}`}>
        {displayValue.toLocaleString()}
        {unit && <span className="text-sm text-muted-foreground ml-1">{unit}</span>}
      </div>
    </div>
  );
}

interface AssumptionSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
}

function AssumptionSlider({ label, value, onChange, min, max, step, unit }: AssumptionSliderProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xl font-display font-bold text-primary">
          {value} {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
        className="w-full"
        aria-label={label}
      />
    </div>
  );
}
